import { Helmet } from 'react-helmet-async';
import seo from '@/seo/routes.json';

// Per-page <head>: title, description, robots, canonical, Open Graph, Twitter.
//
// Static public routes read their copy from src/seo/routes.json — the same
// file scripts/seo-prerender.js reads at build time to write each route's
// HTML file. That keeps the tags in the served HTML and the tags this
// component sets after React boots identical; the tags in public/index.html
// carry data-rh so Helmet replaces them instead of adding a second copy.
//
// A page is indexable only when it declares its own URL (`route` or `path`)
// and is not flagged noindex. Anything else — the logged-in app, the 404
// page, sign-in screens — gets `noindex` and no canonical, so a page can
// never claim another page's URL as its canonical.

const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const NOINDEX_ROBOTS = 'noindex, follow';

// '/' → 'https://nsofar.com/', '/about' → 'https://nsofar.com/about'.
// Already-absolute URLs pass through (blog cover images may be either).
export function absoluteUrl(pathOrUrl) {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return seo.siteUrl + pathOrUrl;
}

export function SEOHead({
  route,
  title,
  description,
  path,
  type = 'website',
  image,
  noindex,
}) {
  const entry = route ? seo.routes[route] : undefined;
  if (process.env.NODE_ENV !== 'production' && route && !entry) {
    // A mistyped route would otherwise fall through to a silent noindex.
    console.error(`SEOHead: route "${route}" is not in src/seo/routes.json`);
  }
  const pagePath = path ?? (entry ? route : undefined);
  const pageTitle = title || entry?.title || seo.defaultTitle;
  const pageDescription = description || entry?.description || seo.defaultDescription;
  const indexable = !(noindex ?? (entry ? !entry.index : false)) && pagePath !== undefined;
  const pageUrl = pagePath !== undefined ? absoluteUrl(pagePath) : undefined;
  const pageImage = image ? absoluteUrl(image) : seo.defaultImage;

  return (
    <Helmet
      // Apply synchronously. Helmet's default waits for an animation frame,
      // which a background tab or a headless renderer may never produce — and
      // for the 404 page this noindex is the only signal a crawler gets.
      defer={false}
      title={pageTitle}
      meta={[
        { name: 'description', content: pageDescription },
        { name: 'robots', content: indexable ? INDEX_ROBOTS : NOINDEX_ROBOTS },
        { property: 'og:type', content: type },
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: pageDescription },
        ...(pageUrl ? [{ property: 'og:url', content: pageUrl }] : []),
        { property: 'og:site_name', content: seo.siteName },
        { property: 'og:image', content: pageImage },
        ...(image ? [] : [
          { property: 'og:image:width', content: String(seo.defaultImageWidth) },
          { property: 'og:image:height', content: String(seo.defaultImageHeight) },
        ]),
        { property: 'og:locale', content: 'en_US' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: pageTitle },
        { name: 'twitter:description', content: pageDescription },
        { name: 'twitter:image', content: pageImage },
      ]}
      link={indexable ? [{ rel: 'canonical', href: pageUrl }] : []}
    />
  );
}
